import { Slot } from 'radix-ui';
import * as React from 'react';

import { Icon } from './Icon';
import { cn } from './utils/cn';

/**
 * Navigation landmark that announces the current location inside a hierarchy.
 * Renders a `<nav aria-label="breadcrumb">` and expects a {@link BreadcrumbList}
 * with {@link BreadcrumbItem} children.
 *
 * @example
 * <Breadcrumb>
 *   <BreadcrumbList>
 *     <BreadcrumbItem>
 *       <BreadcrumbLink asChild>
 *         <Link to="/admin">Admin</Link>
 *       </BreadcrumbLink>
 *     </BreadcrumbItem>
 *     <BreadcrumbSeparator />
 *     <BreadcrumbItem>
 *       <BreadcrumbPage>Organizations</BreadcrumbPage>
 *     </BreadcrumbItem>
 *   </BreadcrumbList>
 * </Breadcrumb>
 */
function Breadcrumb({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      aria-label="breadcrumb"
      data-slot="breadcrumb"
      className={cn(className)}
      {...props}
    />
  );
}

/**
 * Ordered list wrapper for breadcrumb items. Lays the items out horizontally,
 * wraps when the trail overflows, and applies the muted text color used as the
 * resting state for breadcrumb links.
 */
function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'flex flex-wrap items-center gap-1.5 text-sm font-medium wrap-break-word text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Single breadcrumb segment. Holds a {@link BreadcrumbLink},
 * {@link BreadcrumbPage}, or any other content (e.g. a dropdown).
 */
function BreadcrumbItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-1', className)}
      {...props}
    />
  );
}

/**
 * Link inside a breadcrumb. By default renders a plain `<a>`; pass `asChild`
 * to delegate rendering to a routing-library link such as React Router's
 * `<Link>`.
 *
 * @example
 * <BreadcrumbLink asChild>
 *   <Link to="/admin/experiments">Experiments</Link>
 * </BreadcrumbLink>
 */
function BreadcrumbLink({
  asChild,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot.Root : 'a';

  return (
    <Comp
      data-slot="breadcrumb-link"
      className={cn('transition-colors hover:text-foreground', className)}
      {...props}
    />
  );
}

/**
 * Marks the current page in the trail. Renders a non-interactive `<span>` with
 * `aria-current="page"` and the foreground text color, signalling to assistive
 * tech that this segment is the present location.
 */
function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('font-normal text-foreground', className)}
      {...props}
    />
  );
}

/**
 * Visual separator between breadcrumb segments. Defaults to a chevron icon;
 * pass children (e.g. `/`) to override.
 */
function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn(className)}
      {...props}
    >
      {children ?? <Icon name="ChevronRight" size="sm" />}
    </li>
  );
}

/**
 * Collapsed breadcrumb segment shown when the trail is too long to render in
 * full. Renders a horizontal-ellipsis icon with a screen-reader-only label.
 */
function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('flex size-5 items-center justify-center', className)}
      {...props}
    >
      <Icon name="Ellipsis" size="sm" />
      <span className="sr-only">More</span>
    </span>
  );
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
